import {
  GameConfig,
  GameResponse,
  GameEntitlementResponse,
} from '@models/game-config.interface';
import { EconomyResponse, InventoryResponse } from '@models/economy';
import { PlayResponse } from '@models/play-response';
import { SessionParams } from '@models/session-params';
import { Api } from '@services/api/api.interface';
import { fetchWithLogs } from '@services/api/fetch-with-logs';
import { Singleton } from '@utils/singleton';

@Singleton
export class RealApiService implements Api {
  private _token?: string;
  private _timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  setToken(token?: string): void {
    this._token = token;
  }

  async fetchGameConfigUrl(params: SessionParams): Promise<GameResponse> {
    const res = await fetchWithLogs(
      `${import.meta.env.VITE_BFF_URL}/api/game?gameId=${params.gameUuid}&timezone=${this._timezone}`,
      {
        redirect: 'manual',
        ...this.getFetchParams(),
      },
      'Failed to fetch game config',
    );

    // we need to log in if the user doesn't do it yet
    if (res.type === 'opaqueredirect') {
      const location =
        res.headers.get('Location') ?? `${import.meta.env.VITE_BFF_URL}/login`;
      window.location.href = `${location}?redirect=${encodeURIComponent(window.location.href)}`;
      return new Promise(() => {}); // navigation in progress, never resolves
    }

    return (await res.json()) as GameResponse;
  }

  async fetchGameConfig(url: string, fallbackUrl: string): Promise<GameConfig> {
    const tryFetch = async (targetUrl: string): Promise<GameConfig> => {
      const res = await fetchWithLogs(
        targetUrl,
        undefined,
        'Failed to load initial configs',
      );
      const data: GameConfig = await res.json();

      if (!data.game || !data.stage) {
        throw new Error('Failed to load initial configs');
      }

      return data;
    };

    try {
      return await tryFetch(url);
    } catch {
      return await tryFetch(fallbackUrl);
    }
  }

  async fetchGameLabels(
    url: string,
    fallbackUrl: string,
  ): Promise<Record<string, string>> {
    const tryFetch = async (
      targetUrl: string,
    ): Promise<Record<string, string>> => {
      const res = await fetchWithLogs(
        targetUrl,
        undefined,
        'Failed to load labels',
      );
      return (await res.json()) as Record<string, string>;
    };

    try {
      return await tryFetch(url);
    } catch {
      return await tryFetch(fallbackUrl);
    }
  }

  async play(): Promise<PlayResponse> {
    const res = await fetchWithLogs(
      `${import.meta.env.VITE_BFF_URL}/api/game/play?timezone=${this._timezone}`,
      {
        method: 'POST',
        body: undefined,
        ...this.getFetchParams(),
      },
      'Failed to play',
    );

    return (await res.json()) as PlayResponse;
  }

  async resetStage(): Promise<void> {
    await fetchWithLogs(
      `${import.meta.env.VITE_BFF_URL}/api/game/reset`,
      {
        method: 'POST',
        ...this.getFetchParams(),
      },
      'Failed to reset stage',
    );
  }

  async fetchInventory(): Promise<InventoryResponse> {
    const res = await fetchWithLogs(
      `${import.meta.env.VITE_BFF_URL}/api/game/inventory`,
      this.getFetchParams(),
      'Failed to fetch inventory',
    );
    return (await res.json()) as InventoryResponse;
  }

  async fetchEconomy(): Promise<EconomyResponse> {
    const res = await fetchWithLogs(
      `${import.meta.env.VITE_BFF_URL}/api/game/economy`,
      this.getFetchParams(),
      'Failed to fetch economy',
    );
    return (await res.json()) as EconomyResponse;
  }

  async fetchGameState(gameUuid: string): Promise<GameEntitlementResponse> {
    const res = await fetchWithLogs(
      `${import.meta.env.VITE_BFF_URL}/api/game/state?gameId=${gameUuid}&timezone=${this._timezone}`,
      this.getFetchParams(),
      'Failed to fetch game state',
    );
    return (await res.json()) as GameEntitlementResponse;
  }

  private getFetchParams(): RequestInit {
    return {
      credentials: 'include',
      headers: this.getAuthorizationHeader(),
    };
  }

  private getAuthorizationHeader(): HeadersInit {
    if (this._token) {
      return {
        'X-Custom-Authorization': `Bearer ${this._token}`,
      };
    }
    return {};
  }
}
